import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { X, VideoCamera, MapPin, Phone, Users, ArrowSquareOut, Calendar as CalendarIcon } from "@phosphor-icons/react";

/**
 * V1.1 #12 — Native In-App Calendar.
 * FullCalendar month/week/day view of interviews with role-based scoping:
 *   - admin / hiring_manager -> all workspace interviews
 *   - recruiter -> only their own scheduled interviews (backend enforces)
 *
 * Colour rules match the backend: cobalt=virtual, green=f2f, amber=phone,
 * grey=cancelled.
 */
export default function Schedule() {
    const { user } = useAuth();
    const calRef = useRef(null);
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [detail, setDetail] = useState(null);
    // Track viewport so we can stack the calendar toolbar cleanly on mobile.
    // FullCalendar's default header packs prev/next/today + title + view
    // toggle into a single row which overflows on <640px screens.
    const [isMobile, setIsMobile] = useState(
        typeof window !== "undefined" ? window.innerWidth < 640 : false,
    );
    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth < 640);
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);
    const canSeeAll = ["admin", "hiring_manager", "superadmin"].includes(user?.role);

    const fetchEvents = useCallback(async (fetchInfo, successCb, failureCb) => {
        setLoading(true);
        try {
            const { data } = await api.get("/interviews/calendar", {
                params: {
                    start: fetchInfo.startStr,
                    end: fetchInfo.endStr,
                    scope: "auto",
                },
            });
            setEvents(data.events || []);
            successCb(data.events || []);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load calendar");
            failureCb?.(e);
        } finally {
            setLoading(false);
        }
    }, []);

    const openDetail = (info) => {
        const ev = info.event;
        setDetail({
            id: ev.id,
            title: ev.title,
            start: ev.start,
            end: ev.end,
            ...ev.extendedProps,
        });
    };

    // Colour legend (rendered above calendar)
    const legend = useMemo(
        () => [
            { label: "Virtual", color: "#2447F5" },
            { label: "Face to face", color: "#059669" },
            { label: "Telephonic", color: "#B45309" },
            { label: "Cancelled", color: "#A3A3A3" },
        ],
        [],
    );

    return (
        <div className="px-4 sm:px-8 py-6 sm:py-10 max-w-[1600px] mx-auto" data-testid="schedule-page">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
                <div>
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-2">
                        Interviews · Calendar
                    </p>
                    <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter text-ink">
                        Schedule.
                    </h1>
                    <p className="text-sm text-[#525252] mt-2 max-w-2xl">
                        {canSeeAll
                            ? "Every interview across the workspace. Filter by month/week/day."
                            : "Your scheduled interviews. Click any block to view join links + attendees."}
                    </p>
                </div>
                <div
                    data-testid="schedule-legend"
                    className="grid grid-cols-2 gap-x-4 gap-y-1.5 sm:flex sm:flex-wrap sm:items-center sm:gap-x-4 sm:gap-y-2 text-xs"
                >
                    {legend.map((l) => (
                        <span key={l.label} className="inline-flex items-center gap-1.5 whitespace-nowrap">
                            <span
                                className="inline-block w-3 h-3 flex-shrink-0"
                                style={{ backgroundColor: l.color }}
                                aria-hidden
                            />
                            <span className="text-[#525252]">{l.label}</span>
                        </span>
                    ))}
                </div>
            </div>

            <div className="bg-white border border-[#E5E5E5] p-2 sm:p-4 fc-responsive-wrapper">
                <FullCalendar
                    ref={calRef}
                    plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                    initialView="dayGridMonth"
                    headerToolbar={
                        isMobile
                            ? { left: "title", center: "", right: "" }
                            : {
                                left: "prev,next today",
                                center: "title",
                                right: "dayGridMonth,timeGridWeek,timeGridDay",
                            }
                    }
                    footerToolbar={
                        isMobile
                            ? {
                                left: "prev,next today",
                                center: "",
                                right: "dayGridMonth,timeGridWeek,timeGridDay",
                            }
                            : false
                    }
                    titleFormat={isMobile ? { month: "short", year: "numeric" } : undefined}
                    buttonText={{
                        today: "Today",
                        month: "Month",
                        week: "Week",
                        day: "Day",
                    }}
                    height="auto"
                    events={fetchEvents}
                    eventClick={openDetail}
                    eventTimeFormat={{ hour: "numeric", minute: "2-digit", meridiem: "short" }}
                    displayEventTime
                    firstDay={1}
                    nowIndicator
                    dayMaxEvents={isMobile ? 2 : 4}
                />
                {loading && (
                    <p className="p-3 text-xs text-[#A3A3A3]" data-testid="schedule-loading">
                        Loading interviews…
                    </p>
                )}
                {!loading && events.length === 0 && (
                    <div className="p-8 text-center border-t border-[#E5E5E5] mt-2" data-testid="schedule-empty">
                        <CalendarIcon size={28} weight="bold" className="mx-auto text-[#A3A3A3] mb-2" />
                        <p className="font-heading font-black text-lg tracking-tight">Nothing scheduled here.</p>
                        <p className="text-xs text-[#525252] mt-1">
                            Interviews you book will appear on this calendar automatically.
                        </p>
                    </div>
                )}
            </div>

            {detail && <EventDetail detail={detail} onClose={() => setDetail(null)} />}
        </div>
    );
}

function EventDetail({ detail, onClose }) {
    const IconByType = {
        virtual: VideoCamera,
        face_to_face: MapPin,
        telephonic: Phone,
    };
    const Icon = IconByType[detail.interview_type] || CalendarIcon;
    return (
        <div
            className="fixed inset-0 z-40 bg-ink/50 flex items-end sm:items-center justify-center p-0 sm:p-4"
            onClick={onClose}
            data-testid="schedule-event-detail"
        >
            <div
                className="bg-white border border-[#E5E5E5] w-full sm:max-w-lg max-h-[92vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-start justify-between p-5 border-b border-[#E5E5E5]">
                    <div>
                        <p className="text-[10px] font-black uppercase tracking-[0.25em] text-cobalt mb-1 inline-flex items-center gap-1">
                            <Icon size={11} weight="fill" />
                            {(detail.interview_type || "virtual").replace("_", " ")}
                            {detail.status === "cancelled" && (
                                <span className="ml-2 px-1.5 py-0.5 bg-[#F5F5F5] text-[#525252]">CANCELLED</span>
                            )}
                        </p>
                        <h2 className="font-heading text-xl font-black tracking-tight text-ink">
                            {detail.title}
                        </h2>
                    </div>
                    <button
                        onClick={onClose}
                        aria-label="Close"
                        className="p-2 hover:bg-[#F5F5F5]"
                        data-testid="schedule-event-close"
                    >
                        <X size={18} weight="bold" />
                    </button>
                </div>
                <div className="p-5 space-y-4 text-sm">
                    <div>
                        <p className="text-[10px] font-black uppercase tracking-wider text-[#A3A3A3]">When</p>
                        <p className="mt-0.5 text-ink">
                            {detail.start && new Date(detail.start).toLocaleString()}
                            {detail.end && ` — ${new Date(detail.end).toLocaleTimeString()}`}
                        </p>
                        {detail.timezone && (
                            <p className="text-xs text-[#A3A3A3]">{detail.timezone}</p>
                        )}
                    </div>
                    {detail.candidate_name && (
                        <div>
                            <p className="text-[10px] font-black uppercase tracking-wider text-[#A3A3A3]">Candidate</p>
                            <p className="mt-0.5 text-ink font-bold">{detail.candidate_name}</p>
                            <p className="text-xs text-[#525252]">{detail.candidate_email}</p>
                            {detail.candidate_id && (
                                <Link
                                    to={`/candidates/${detail.candidate_id}`}
                                    className="inline-flex items-center gap-1 mt-1 text-xs font-bold text-cobalt hover:text-ink"
                                >
                                    Open profile <ArrowSquareOut size={11} weight="bold" />
                                </Link>
                            )}
                        </div>
                    )}
                    {detail.job_title && (
                        <div>
                            <p className="text-[10px] font-black uppercase tracking-wider text-[#A3A3A3]">Role</p>
                            <p className="mt-0.5 text-ink">{detail.job_title}</p>
                        </div>
                    )}
                    {detail.hiring_manager_name && (
                        <div>
                            <p className="text-[10px] font-black uppercase tracking-wider text-[#A3A3A3]">Hiring manager</p>
                            <p className="mt-0.5 text-ink">{detail.hiring_manager_name}</p>
                        </div>
                    )}
                    {detail.interview_type === "virtual" && detail.meet_link && (
                        <div>
                            <p className="text-[10px] font-black uppercase tracking-wider text-[#A3A3A3]">Join</p>
                            <a
                                href={detail.meet_link}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="schedule-event-join"
                                className="mt-1 inline-flex items-center gap-2 bg-cobalt hover:bg-cobalt-dark text-white px-4 py-2 text-xs font-black uppercase tracking-wider"
                            >
                                <VideoCamera size={12} weight="bold" /> Open meet link
                            </a>
                            <p className="mt-1 text-[10px] text-[#A3A3A3] break-all">{detail.meet_link}</p>
                        </div>
                    )}
                    {detail.interview_type === "face_to_face" && detail.location && (
                        <div>
                            <p className="text-[10px] font-black uppercase tracking-wider text-[#A3A3A3]">Location</p>
                            <p className="mt-0.5 text-ink">{detail.location}</p>
                        </div>
                    )}
                    {detail.interview_type === "telephonic" && detail.phone_number && (
                        <div>
                            <p className="text-[10px] font-black uppercase tracking-wider text-[#A3A3A3]">Phone</p>
                            <p className="mt-0.5 text-ink">{detail.phone_number}</p>
                        </div>
                    )}
                    {detail.attendees?.length > 0 && (
                        <div>
                            <p className="text-[10px] font-black uppercase tracking-wider text-[#A3A3A3] flex items-center gap-1">
                                <Users size={11} weight="fill" /> Attendees
                            </p>
                            <ul className="mt-1 space-y-0.5 text-xs text-[#525252]">
                                {detail.attendees.map((a) => (
                                    <li key={a}>{a}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
